/**
 * When it runs (spec §2 E).
 *
 * Manual triggers run in the browser. Events, the schedule and tasks run on
 * the server: database triggers record events in agent_events, and the
 * ambient event_watch and agent_run jobs run the agent as itself plus its
 * sponsor. The platform owns the event list; this agent's type declares the
 * subset it supports; an administrator turns each one on or off.
 */

import { useEffect, useState } from 'react';
import { Button } from '../../ui/Button';
import { Callout, Select } from '../../ds';
import { AGENT_BEHAVIORS, AGENT_EVENT_CATALOG } from '../../../lib/ai/agentDefinitions';
import { getSupabaseClient } from '../../../lib/supabaseClient';
import { assignTaskToAgent } from '../../../lib/agentRuntimeService';
import { SwitchGroup, SwitchRow, Toggle, type SectionProps } from './shared';
import { resolveTriggers, type ResolvedTriggers } from './resolve';

const CADENCES: { minutes: number | null; label: string }[] = [
  { minutes: null, label: 'No schedule' },
  { minutes: 15, label: 'Every 15 minutes' },
  { minutes: 60, label: 'Every hour' },
  { minutes: 240, label: 'Every 4 hours' },
  { minutes: 1440, label: 'Every day' },
  { minutes: 10080, label: 'Every week' },
];

export function TriggersSection({ def, config, workspaceId, isSaving, canEdit, save, manualSingleHelp, allEmptyLabel }: SectionProps & { manualSingleHelp: string; allEmptyLabel: string }) {
  const t = resolveTriggers(def, config);
  const put = (patch: Partial<ResolvedTriggers>, ok?: string) => save({ triggers: { ...t, ...patch } }, ok);
  const [debounce, setDebounce] = useState(String(t.debounceSeconds));
  useEffect(() => setDebounce(String(t.debounceSeconds)), [t.debounceSeconds]);
  const events = def.supportedEvents.filter((e) => e !== 'task_assigned');

  // An agent that runs only when a person uses it has no batch, events, schedule or tasks to switch.
  if (!AGENT_BEHAVIORS[def.behaviorKind].runsUnattended) {
    return (
      <SwitchGroup title="When it runs" lead="Only when a person uses it. It has no batch runs, events, schedule or tasks.">
        <SwitchRow label="When a person uses it" help={manualSingleHelp} on={t.manualSingle} disabled={isSaving || !canEdit} onChange={() => void put({ manualSingle: !t.manualSingle })} />
      </SwitchGroup>
    );
  }

  return (
    <SwitchGroup title="When it runs" lead="Manual runs happen in your browser. Events, the schedule and tasks run on the server, as the agent plus its sponsor.">
      <SwitchRow label="From one item" help={manualSingleHelp} on={t.manualSingle} disabled={isSaving || !canEdit} onChange={() => void put({ manualSingle: !t.manualSingle })} />
      <SwitchRow label="On a batch" help="Run over a scope from this page." on={t.manualBulk} disabled={isSaving || !canEdit} onChange={() => void put({ manualBulk: !t.manualBulk })} />
      <SwitchRow label="Run now from the agents page" help="The Run now link on its card." on={t.runNow} disabled={isSaving || !canEdit} onChange={() => void put({ runNow: !t.runNow })} />

      <div className="border-t border-[var(--rule)] pt-3 space-y-2">
        <p className="text-sm font-medium text-[var(--text-pri)]">On these events</p>
        <p className="text-[11px] text-[var(--text-ter)]">
          Checked every few minutes. {def.triggers.events.length
            ? `${def.triggers.events.map((e) => `“${AGENT_EVENT_CATALOG[e].label}”`).join(' and ')} starts on, so it runs by itself; the others start off.`
            : 'Off by default.'}
        </p>
        {events.map((e) => {
          const on = t.events.includes(e);
          return (
            <div key={e} className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm text-[var(--text-pri)]">{AGENT_EVENT_CATALOG[e].label}</p>
                <p className="text-[11px] text-[var(--text-ter)]">{AGENT_EVENT_CATALOG[e].description}</p>
              </div>
              <Toggle on={on} disabled={isSaving || !canEdit} label={AGENT_EVENT_CATALOG[e].label}
                onChange={() => void put({ events: on ? t.events.filter((x) => x !== e) : [...t.events, e] })} />
            </div>
          );
        })}
        <div className="flex items-center gap-2 pt-1">
          <label className="text-xs text-[var(--text-sec)]" htmlFor={`debounce-${def.agentType}`}>Wait after the last change (seconds)</label>
          <input id={`debounce-${def.agentType}`} type="number" min={0} max={3600} value={debounce} readOnly={!canEdit}
            onChange={(e) => setDebounce(e.target.value)}
            onBlur={() => { const n = Math.max(0, Math.min(3600, Math.round(Number(debounce) || 0))); if (n !== t.debounceSeconds) void put({ debounceSeconds: n }); }}
            className="w-24 px-2 py-1 text-sm border border-[var(--rule)] bg-[var(--surface)]" />
        </div>
      </div>

      <div className="border-t border-[var(--rule)] pt-3 space-y-2">
        <p className="text-sm font-medium text-[var(--text-pri)]">On a schedule</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Select value={t.scheduleMinutes == null ? '' : String(t.scheduleMinutes)} disabled={isSaving || !canEdit}
            onChange={(e) => void put({ scheduleMinutes: e.target.value ? Number(e.target.value) : null })} aria-label="Schedule">
            {CADENCES.map((c) => <option key={String(c.minutes)} value={c.minutes == null ? '' : String(c.minutes)}>{c.label}</option>)}
          </Select>
          <Select value={config?.scopeDefault === 'since_last_run' ? 'since_last_run' : 'all_empty'} disabled={isSaving || !canEdit || t.scheduleMinutes == null}
            onChange={(e) => void save({ scopeDefault: e.target.value })} aria-label="What a scheduled run covers">
            <option value="all_empty">{allEmptyLabel}</option>
            <option value="since_last_run">Only items new since the last run</option>
          </Select>
        </div>
      </div>

      {def.supportedEvents.includes('task_assigned') && (
        <>
          <SwitchRow label="Takes tasks" help="People can give it a task over a set of items. The task completes when a person has reviewed what it produced."
            on={t.taskAssigned} disabled={isSaving || !canEdit} onChange={() => void put({ taskAssigned: !t.taskAssigned })} />
          {t.taskAssigned && config?.enabled && <GiveTask agentType={def.agentType} agentName={def.name} workspaceId={workspaceId} />}
        </>
      )}
    </SwitchGroup>
  );
}

/** Any member can give an agent that takes tasks a task over one pool's items. */
function GiveTask({ agentType, agentName, workspaceId }: { agentType: string; agentName: string; workspaceId: string }) {
  const [pools, setPools] = useState<{ id: string; name: string; ids: string[] }[]>([]);
  const [poolId, setPoolId] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'green' | 'coral'; text: string } | null>(null);

  useEffect(() => {
    void (async () => {
      const { data } = await getSupabaseClient().from('item_pools').select('id, name, priority_ids').eq('workspace_id', workspaceId).order('name').limit(200);
      setPools(((data ?? []) as Array<{ id: string; name: string | null; priority_ids: string[] | null }>)
        .map((p) => ({ id: p.id, name: p.name || 'Untitled', ids: p.priority_ids ?? [] }))
        .filter((p) => p.ids.length > 0));
    })();
  }, [workspaceId]);

  const pool = pools.find((p) => p.id === poolId);
  const give = async () => {
    if (!pool) return;
    setBusy(true);
    setMessage(null);
    const res = await assignTaskToAgent({ workspaceId, agentType, itemIds: pool.ids, name: `${agentName}: ${pool.name}` });
    setBusy(false);
    setMessage(res.error ? { tone: 'coral', text: res.error } : { tone: 'green', text: `Task given. ${agentName} will pick it up within a few minutes.` });
  };

  return (
    <div className="border border-[var(--rule)] p-3 space-y-2">
      <p className="text-xs font-medium text-[var(--text-sec)]">Give it a task</p>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={poolId} onChange={(e) => setPoolId(e.target.value)} aria-label="Pool" className="min-w-[14rem]">
          <option value="">Choose a pool…</option>
          {pools.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.ids.length})</option>)}
        </Select>
        {pool && <Button variant="secondary" size="sm" disabled={busy} onClick={() => void give()}>Give the task</Button>}
      </div>
      {message && <Callout tone={message.tone}>{message.text}</Callout>}
    </div>
  );
}
